import classNames from "classnames";
import { IconButton } from "client/Components";
import { HubItemRowLayoutKind } from "client/Api/Hub/common";
import { BlobIdImageElementComposerViewModel } from "client/Api/Hub/common.composer";
import { IValueInputAttachable } from "client/Forms/attachable";
import { getClientId, ObjectWithClientId } from "client/Misc/clientId";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { Guid } from "client_legacy/ndaw/guid";
import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { EmbedInputDialogData } from "../../HubMediaSelect/EmbedInputDialog";
import { InlineActionList } from "../Actions/InlineActionList";
import { Column, ColumnHandle } from "./Column";
import styles from "./Row.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { VideoInputDialogData } from "../../HubMediaSelect/VideoInputDialog";
import { HubItemColumnFormModel, HubItemRowFormModel } from "../../Management/common";
import { ImageWithAttribution, HubImageSelectorKind } from "../../Management/useHubImageSelector";

export type RowProps = {
	hubItemKind: HubItemKind;
	layoutLocation: { row: number; };
	attachable: IValueInputAttachable<ObjectWithClientId<HubItemRowFormModel>>;
	originalImages: Map<number, BlobIdImageElementComposerViewModel>;
	onDelete(): void;
	onMoveUp?(): void;
	onMoveDown?(): void;
	onDuplicate(): void;
	hasStackedColumns: boolean;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
};

export type RowHandle = {
	findAndFocus(clientId: Guid): void;
	scrollIntoView(): void;
};

export const Row = forwardRef(
	(props: RowProps, ref: Ref<RowHandle>): ReactElement => {
		const {
			hubItemKind,
			layoutLocation,
			attachable,
			originalImages,
			onDelete,
			onDuplicate,
			onMoveDown,
			onMoveUp,
			hasStackedColumns,
			onOpenEmbedInputDialog,
			onOpenVideoInputDialog,
			onSelectImage
		} = props;

		const [isActionsDropOverVisible, setIsActionsDropOverVisible] = useState<boolean>(false);

		const containerRef = useRef<HTMLDivElement>(null);
		const [columnToScrollToId, setColumnToScrollToId] = useState<Guid | undefined>(undefined);
		const columnRefs: ReadonlyRefObject<Map<Guid, (ColumnHandle | null)>> =
			useRef<Map<Guid, (ColumnHandle | null)>>(new Map());

		const { layout } = attachable.value;

		const setColumnRef = useCallback(
			(column: ColumnHandle | null, id: Guid): void => {
				columnRefs.current
					.set(id, column);
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): RowHandle => ({
				findAndFocus(clientId: Guid): void {
					columnRefs.current
						.forEach((x) => x?.findAndFocus(clientId));
				},
				scrollIntoView(): void {
					containerRef.current
						?.scrollIntoView();
				}
			}),
			[]
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				const columnsCopy: ObjectWithClientId<HubItemColumnFormModel>[] = [
					...attachable.for("columns")
						.value
				];
				const itemToMove: ObjectWithClientId<HubItemColumnFormModel> = columnsCopy.splice(fromIndex, 1)[0];

				columnsCopy.splice(
					toIndex,
					0,
					itemToMove
				);

				let newLayout: HubItemRowLayoutKind = layout;

				if (layout === HubItemRowLayoutKind.TwoColumnBigLeft) {
					newLayout = HubItemRowLayoutKind.TwoColumnBigRight;
				} else if (layout === HubItemRowLayoutKind.TwoColumnBigRight) {
					newLayout = HubItemRowLayoutKind.TwoColumnBigLeft;
				}

				attachable.reducer
					.setValue({
						...attachable.value,
						layout: newLayout,
						columns: columnsCopy
					});

				setColumnToScrollToId(
					getClientId(itemToMove)
				);

				attachable.validation
					.triggerInputFinished();
			},
			[attachable, layout]
		);

		useLayoutEffect(
			(): void => {
				if (isNotNil(columnToScrollToId)) {
					columnRefs.current
						.get(columnToScrollToId)
						?.scrollIntoView();

					setColumnToScrollToId(undefined);
				}
			},
			[columnToScrollToId]
		);

		return (
			<div
				{...setTrackingId(attachable.reducer.path)}
				className={styles.root}
				tabIndex={1}
				onFocus={(e) => {
					e.stopPropagation();
					setIsActionsDropOverVisible(true);
				}}
				onBlur={() => setIsActionsDropOverVisible(false)}
			>
				<div className={styles.actionsDropOverAnchor}>
					{isActionsDropOverVisible && (
						<RowDropOverActions
							onDelete={onDelete}
							onDuplicate={onDuplicate}
							onMoveDown={onMoveDown}
							onMoveUp={onMoveUp}
						/>
					)}
				</div>
				<div
					className={classNames(
						styles.innerRow,
						classNameForRowLayoutKindMap[layout],
						{
							[styles.stackedColumns]: hasStackedColumns
						}
					)}
					ref={containerRef}
				>
					{attachable.for("columns")
						.map((columnAttachable, index) => {
							const clientId: Guid = getClientId(columnAttachable.value);
							return (
								<Column
									key={clientId}
									layoutLocation={{ ...layoutLocation, column: index }}
									ref={c => setColumnRef(c, clientId)}
									hubItemKind={hubItemKind}
									attachable={columnAttachable}
									originalImages={originalImages}
									onOpenEmbedInputDialog={onOpenEmbedInputDialog}
									onOpenVideoInputDialog={onOpenVideoInputDialog}
									onMoveRight={index < attachable.for("columns").value.length - 1
										? () => itemMoveHandler(index, index + 1)
										: undefined
									}
									onMoveLeft={index > 0
										? () => itemMoveHandler(index, index - 1)
										: undefined
									}
									onSelectImage={onSelectImage}
								/>
							);
						})
					}
				</div>
			</div>
		);
	}
);
Row.displayName="Row";

type RowDropOverActionsProps = {
	onDelete(): void;
	onMoveUp?(): void;
	onMoveDown?(): void;
	onDuplicate(): void;
};

const RowDropOverActions = (props: RowDropOverActionsProps): ReactElement => {
	const {
		onDelete,
		onMoveUp,
		onMoveDown,
		onDuplicate
	} = props;

	return (
		<div className={styles.actionsDropOver}>
			<InlineActionList trackingId="row-actions">
				{onMoveUp && (
					<IconButton
						kind="transparent-colored"
						icon="arrow-up"
						onClick={onMoveUp}
						trackingId="move-up-button"
					/>
				)}
				{onMoveDown && (
					<IconButton
						kind="transparent-colored"
						icon="arrow-down"
						onClick={onMoveDown}
						trackingId="move-down-button"
					/>
				)}
				<IconButton
					kind="transparent-colored"
					icon="copy"
					onClick={onDuplicate}
					trackingId="copy-button"
				/>
				<IconButton
					kind="transparent-colored"
					icon="trash-can"
					onClick={onDelete}
					trackingId="delete-button"
				/>
			</InlineActionList>
		</div>
	);
};

RowDropOverActions.displayName="RowDropOverActions";

const classNameForRowLayoutKindMap: {[key in HubItemRowLayoutKind]: string | undefined } = {
	[HubItemRowLayoutKind.OneColumn]: undefined,
	[HubItemRowLayoutKind.TwoColumn]: undefined,
	[HubItemRowLayoutKind.ThreeColumn]: undefined,
	[HubItemRowLayoutKind.FourColumn]: undefined,
	[HubItemRowLayoutKind.TwoColumnBigLeft]: styles.innerRow_twoColumnBigLeft,
	[HubItemRowLayoutKind.TwoColumnBigRight]: styles.innerRow_twoColumnBigRight
};

export const rowLayoutToNumberOfColumnsMap: {[key in HubItemRowLayoutKind]: number;} = {
	[HubItemRowLayoutKind.OneColumn]: 1,
	[HubItemRowLayoutKind.TwoColumn]: 2,
	[HubItemRowLayoutKind.TwoColumnBigLeft]: 2,
	[HubItemRowLayoutKind.TwoColumnBigRight]: 2,
	[HubItemRowLayoutKind.ThreeColumn]: 3,
	[HubItemRowLayoutKind.FourColumn]: 4
};
